import React, { useState, useEffect, useRef } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  HomeIcon,
  DocumentTextIcon,
  PlusCircleIcon,
  DocumentDuplicateIcon,
  Cog6ToothIcon,
  QuestionMarkCircleIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  SparklesIcon,
  UserCircleIcon,
  BriefcaseIcon,
  UserIcon,
  DocumentMagnifyingGlassIcon,
  ChevronDownIcon,
  CurrencyDollarIcon,
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  BugAntIcon,
} from "@heroicons/react/24/outline";
import { ROUTES } from "@/shared/core";
import { useAuth } from "@/contexts/AuthContext";
import { useReportError } from "@/contexts/ReportErrorContext";
import { usePlanLimits } from "@/hooks/usePlanLimits";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import type { PlanType } from "@/shared/db";
import type { Notification } from "@/shared/ui/types/NotificationCenter.types";
import { EmailVerificationBanner } from "../components/EmailVerificationBanner";
import { GlobalSearch } from "../components/GlobalSearch";
import { NotificationCenter } from "@/shared/ui";
import { useNotifications } from "../hooks/useNotifications";
import { cn } from "../lib/utils";
import type { SidebarItemProps } from "./types/layout.types";

const SidebarItem: React.FC<SidebarItemProps> = ({
  icon: Icon,
  label,
  badge,
  isActive,
  onClick,
}) => {
  return (
    <motion.button
      onClick={onClick}
      whileHover={{ x: 4 }}
      whileTap={{ scale: 0.98 }}
      className={cn(
        "w-full flex items-center justify-between text-left px-4 py-3 rounded-lg cursor-pointer transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-tiber-500 focus-visible:ring-offset-2",
        isActive
          ? "border-2 border-tiber-900 text-tiber-900 shadow-md"
          : "text-tiber-900 hover:bg-romance-50 hover:shadow-md",
      )}
    >
      <div className="flex items-center space-x-3">
        <Icon className="w-5 h-5" />
        <span className="font-medium">{label}</span>
      </div>
      {badge && (
        <span
          className={cn(
            "px-2 py-0.5 rounded-full text-xs font-bold",
            isActive
              ? "bg-white/20 text-white"
              : "bg-tiber-500/10 text-tiber-500",
          )}
        >
          {badge}
        </span>
      )}
    </motion.button>
  );
};

// Helper function to format plan names for display
const formatPlanName = (planType: string | null): string => {
  if (!planType) return "Free";
  return planType
    .replace(/^lawyer_/, "") // Remove lawyer_ prefix
    .replace(/_/g, " ") // Replace underscores with spaces
    .replace(/\b\w/g, (l) => l.toUpperCase()); // Capitalize first letter of each word
};

// Helper function to get next available plan based on current plan and user type
const getNextPlan = (
  currentPlanType: PlanType | null,
  userType: string | null,
): PlanType | null => {
  if (!currentPlanType || !userType) return null;

  const planHierarchy: Record<string, PlanType[]> = {
    lawyer: ["lawyer_free", "lawyer_professional", "lawyer_professional_pro"],
  };

  const plans = planHierarchy[userType] || [];
  const currentIndex = plans.indexOf(currentPlanType);

  if (currentIndex === -1 || currentIndex === plans.length - 1) {
    return null; // Already on highest plan or plan not found
  }

  return plans[currentIndex + 1];
};

export const DashboardLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { profile, signOut, user, loadingProfile, loading } = useAuth();
  const { subscription, limits } = usePlanLimits();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Notifications
  const {
    notifications,
    unreadCount,
    loading: notificationsLoading,
    markAsRead,
    markAllAsRead,
    deleteNotif,
  } = useNotifications(user?.id);

  const handleNotificationClick = (notification: Notification) => {
    if (notification.link) {
      navigate(notification.link);
    }
  };

  // Generate sidebar items based on user type
  const getSidebarItems = (): SidebarItemProps[] => {
    // Wait for profile to load before determining sidebar
    if (loadingProfile || loading || !profile) {
      // Return empty array while loading - will show loading state
      return [];
    }

    const userType = profile.user_type;

    // Lawyer-specific sidebar
    if (userType === "lawyer") {
      const isFreePlan = subscription?.planType === "lawyer_free";
      const items: SidebarItemProps[] = [
        {
          icon: HomeIcon,
          label: t("lawyer.sidebar.dashboard"),
          href: "/dashboard/lawyer",
        },
        {
          icon: DocumentTextIcon,
          label: t("dashboard.sidebar.myDocuments"),
          href: "/dashboard/lawyer/documents",
        },
        ...(isFreePlan
          ? []
          : [
              {
                icon: BriefcaseIcon,
                label: t("lawyer.sidebar.cases"),
                href: "/dashboard/lawyer/cases",
              },
              {
                icon: UserIcon,
                label: t("lawyer.sidebar.clients"),
                href: "/dashboard/lawyer/clients",
              },
            ]),
        {
          icon: QuestionMarkCircleIcon,
          label: t("lawyer.sidebar.consultations"),
          href: "/dashboard/lawyer/consultations",
        },
        {
          icon: DocumentMagnifyingGlassIcon,
          label: t("documentReview.title"),
          href: "/dashboard/lawyer/document-review",
        },
        {
          icon: ChatBubbleLeftRightIcon,
          label: t("legalChat.title"),
          href: "/dashboard/lawyer/legal-chat",
        },
        ...(!isFreePlan
          ? [
              {
                icon: SparklesIcon,
                label: t("lawyer.sidebar.caseSummary"),
                href: "/dashboard/lawyer/case-summary",
              },
            ]
          : []),
        ...(limits?.has_billing_reports && !isFreePlan
          ? [
              {
                icon: ChartBarIcon,
                label: t("analytics.sidebar"),
                href: "/dashboard/lawyer/billing-reports",
              },
            ]
          : []),
        {
          icon: PlusCircleIcon,
          label: t("dashboard.sidebar.generate"),
          href: ROUTES.GENERATE,
        },
        {
          icon: UserIcon,
          label: t("lawyer.sidebar.profile"),
          href: "/dashboard/lawyer/profile/edit",
        },
        {
          icon: Cog6ToothIcon,
          label: t("dashboard.sidebar.settings"),
          href: "/settings",
        },
      ];
      return items;
    }

    // Default sidebar for other user types
    return [
      {
        icon: HomeIcon,
        label: t("dashboard.sidebar.home"),
        href: ROUTES.DASHBOARD,
      },
      {
        icon: DocumentTextIcon,
        label: t("dashboard.sidebar.myDocuments"),
        href: ROUTES.DASHBOARD,
      },
      {
        icon: PlusCircleIcon,
        label: t("dashboard.sidebar.generate"),
        href: ROUTES.GENERATE,
      },
      {
        icon: DocumentDuplicateIcon,
        label: t("dashboard.sidebar.templates"),
        href: "/templates",
      },
      {
        icon: Cog6ToothIcon,
        label: t("dashboard.sidebar.settings"),
        href: "/settings",
      },
      {
        icon: QuestionMarkCircleIcon,
        label: t("dashboard.sidebar.help"),
        href: "/help",
      },
    ];
  };

  const sidebarItems = getSidebarItems();

  const handleNavigation = (href: string) => {
    navigate(href);
    setIsSidebarOpen(false);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate(ROUTES.HOME);
  };

  const openReportModal = useReportError();

  // Get profile route based on user type
  const getProfileRoute = (): string => {
    if (!profile?.user_type) return "/settings";
    const userType = profile.user_type;
    if (userType === "student") return "/dashboard/student/profile";
    if (userType === "lawyer") return "/dashboard/lawyer/profile/edit";
    return "/settings";
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isUserMenuOpen]);

  // Close user menu when navigating
  useEffect(() => {
    setIsUserMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className="h-screen bg-nieve flex flex-col overflow-hidden">
      {/* Top Navbar */}
      <nav className="bg-white border-b border-tiber-200 z-40 shrink-0">
        <div className="px-2 sm:px-4 lg:px-8 min-w-0">
          <div className="flex items-center justify-between h-12 sm:h-16 gap-1 sm:gap-2 min-w-0">
            {/* Left: Logo + Mobile Menu */}
            <div className="flex items-center gap-1 sm:gap-4 shrink-0 min-w-0">
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="lg:hidden p-1.5 sm:p-2 rounded-lg hover:bg-romance-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-tiber-500 focus-visible:ring-offset-2 transition-colors shrink-0"
                aria-label="Toggle sidebar"
              >
                {isSidebarOpen ? (
                  <XMarkIcon className="w-5 h-5 text-tiber-900" aria-hidden />
                ) : (
                  <Bars3Icon className="w-5 h-5 text-tiber-900" aria-hidden />
                )}
              </button>

              <div
                className="flex items-center gap-1 sm:gap-2 cursor-pointer shrink-0 min-w-0"
                onClick={() => navigate(ROUTES.HOME)}
              >
                <img
                  src="/images/brand/lawgentia-logo.png"
                  className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 shrink-0"
                  alt="Lawgentia Logo"
                />
                <span className="hidden sm:inline text-base lg:text-xl font-bold text-tiber-800 truncate">
                  Lawgentia
                </span>
              </div>
            </div>

            {/* Center: Global Search - hidden on small, visible md+ */}
            <div className="flex-1 max-w-md mx-2 lg:mx-4 min-w-0 hidden md:block">
              <GlobalSearch />
            </div>

            {/* Right: Search (mobile) + Language + Notifications + User */}
            <div className="flex items-center gap-1 sm:gap-2 lg:gap-4 shrink-0 min-w-0">
              <div className="md:hidden shrink-0">
                <GlobalSearch compact />
              </div>
              <LanguageSwitcher className="shrink-0 px-2! py-2! sm:px-3! sm:py-3!" />

              {/* Notification Center */}
              <NotificationCenter
                notifications={notifications}
                unreadCount={unreadCount}
                loading={notificationsLoading}
                onMarkAsRead={markAsRead}
                onMarkAllAsRead={markAllAsRead}
                onDelete={deleteNotif}
                onNotificationClick={(notification: Notification) =>
                  handleNotificationClick(notification)
                }
              />

              {/* User Menu */}
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center gap-2 sm:gap-4 px-2 sm:px-3 py-2 rounded-lg hover:bg-romance-50 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-tiber-500 focus-visible:ring-offset-2 cursor-pointer shrink-0 min-w-0"
                  aria-label="User menu"
                  aria-expanded={isUserMenuOpen}
                >
                  <div className="hidden md:block text-right">
                    <p className="text-base font-bold text-juridico">
                      {profile?.full_name ||
                        (profile?.email
                          ? profile.email
                              .split("@")[0]
                              .replace(/[._]/g, " ")
                              .replace(/\b\w/g, (c: string) => c.toUpperCase())
                          : t("dashboard.userMenu.user"))}
                    </p>
                    <div className="flex items-center justify-end gap-1.5 mt-0.5">
                      {profile?.user_type === "lawyer" ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-linear-to-r from-dorado to-dorado-dark text-white text-xs font-bold">
                          ✨{" "}
                          {formatPlanName(
                            subscription?.planType || profile?.plan,
                          )}
                        </span>
                      ) : profile?.plan === "pro" ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-linear-to-r from-dorado to-dorado-dark text-white text-xs font-bold">
                          ✨ {t("dashboard.userMenu.pro")}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-tiber-500/10 text-tiber-500 text-xs font-semibold">
                          <CurrencyDollarIcon className="w-3.5 h-3.5" />
                          {profile?.credits_remaining || 0}{" "}
                          {t("dashboard.userMenu.credits")}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="relative shrink-0">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-full bg-linear-to-br from-tiber-500 to-tiber-600 flex items-center justify-center shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden shrink-0">
                      {profile?.profile_image_url ? (
                        <img
                          src={profile.profile_image_url}
                          alt={profile.full_name || "User profile"}
                          className="w-full h-full object-cover"
                        />
                      ) : profile?.user_type === "lawyer" ? (
                        <img
                          src="/images/brand/lawgentia-logo-white.png"
                          alt="Lawgentia"
                          className="w-8 h-8 object-contain"
                        />
                      ) : (
                        <UserCircleIcon className="w-7 h-7 text-white" />
                      )}
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-white border-2 border-white flex items-center justify-center">
                      <ChevronDownIcon
                        className={cn(
                          "w-3 h-3 text-tiber-500 transition-transform duration-200",
                          isUserMenuOpen && "transform rotate-180",
                        )}
                      />
                    </div>
                  </div>
                </button>

                {/* Dropdown Menu */}
                <AnimatePresence>
                  {isUserMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 mt-2 w-56 min-w-[200px] max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-xl border border-tiber-200 py-2 z-50"
                    >
                      {/* User Info Header */}
                      <div className="px-4 py-3 border-b border-tiber-100">
                        <p className="text-sm font-bold text-juridico">
                          {profile?.full_name ||
                            (profile?.email
                              ? profile.email
                                  .split("@")[0]
                                  .replace(/[._]/g, " ")
                                  .replace(/\b\w/g, (c: string) =>
                                    c.toUpperCase(),
                                  )
                              : t("dashboard.userMenu.user"))}
                        </p>
                        <p className="text-xs text-titan mt-0.5">
                          {profile?.email}
                        </p>
                        {profile?.user_type === "lawyer" ? (
                          <span className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full bg-linear-to-r from-dorado to-dorado-dark text-white text-xs font-bold">
                            ✨{" "}
                            {formatPlanName(
                              subscription?.planType || profile?.plan,
                            )}
                          </span>
                        ) : profile?.plan === "pro" ? (
                          <span className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full bg-linear-to-r from-dorado to-dorado-dark text-white text-xs font-bold">
                            ✨ {t("dashboard.userMenu.pro")}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 mt-2 px-2.5 py-1 rounded-full bg-tiber-500/10 text-tiber-500 text-xs font-semibold">
                            <CurrencyDollarIcon className="w-3.5 h-3.5" />
                            {profile?.credits_remaining || 0}{" "}
                            {t("dashboard.userMenu.credits")}
                          </span>
                        )}
                      </div>

                      {/* Menu Items */}
                      <div className="py-1">
                        <button
                          onClick={() => {
                            navigate(getProfileRoute());
                            setIsUserMenuOpen(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-juridico hover:bg-romance-50 transition-colors duration-150"
                        >
                          <UserIcon className="w-5 h-5 text-tiber-500" />
                          <span className="font-medium">
                            {t("dashboard.userMenu.profile")}
                          </span>
                        </button>
                        <button
                          onClick={() => {
                            navigate("/settings");
                            setIsUserMenuOpen(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-juridico hover:bg-romance-50 transition-colors duration-150"
                        >
                          <Cog6ToothIcon className="w-5 h-5 text-tiber-500" />
                          <span className="font-medium">
                            {t("dashboard.sidebar.settings")}
                          </span>
                        </button>
                        <button
                          onClick={() => {
                            openReportModal();
                            setIsUserMenuOpen(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-juridico hover:bg-romance-50 transition-colors duration-150"
                        >
                          <BugAntIcon className="w-5 h-5 text-tiber-500" />
                          <span className="font-medium">
                            {t("errorBoundary.reportError")}
                          </span>
                        </button>
                      </div>

                      {/* Separator */}
                      <div className="border-t border-tiber-100 my-1"></div>

                      {/* Sign Out */}
                      <div className="py-1">
                        <button
                          onClick={() => {
                            setIsUserMenuOpen(false);
                            handleSignOut();
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors duration-150"
                        >
                          <ArrowRightOnRectangleIcon className="w-5 h-5" />
                          <span className="font-medium">
                            {t("nav.signOut")}
                          </span>
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - Desktop */}
        {!loadingProfile && !loading && profile && (
          <aside className="hidden lg:block w-64 bg-white border-r border-tiber-200 h-full overflow-y-auto shrink-0">
            <div className="p-4 space-y-2">
              {sidebarItems.map((item, index) => (
                <SidebarItem
                  key={index}
                  {...item}
                  isActive={location.pathname === item.href}
                  onClick={() => handleNavigation(item.href || "")}
                />
              ))}

              {/* Upgrade Banner - Smart */}
              {(() => {
                const currentPlanType = subscription?.planType || null;
                const userType = profile?.user_type || null;
                const nextPlan = getNextPlan(currentPlanType, userType);

                if (!nextPlan) return null; // Already on highest plan or no plan data

                const planName = formatPlanName(nextPlan);

                return (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="mt-6 p-4 bg-linear-to-br from-dorado to-dorado-dark rounded-xl text-white"
                  >
                    <h3 className="font-bold mb-2">
                      ✨ {t("dashboard.sidebar.upgrade", { plan: planName })}
                    </h3>
                    <p className="text-sm mb-3 opacity-90">
                      {t("dashboard.sidebar.upgradeMessage", {
                        plan: planName,
                      })}
                    </p>
                    <button
                      onClick={() => navigate(ROUTES.PRICING)}
                      className="w-full bg-white text-dorado font-semibold py-2 rounded-lg hover:bg-romance-50 transition-colors"
                    >
                      {t("dashboard.sidebar.upgradeButton")}
                    </button>
                  </motion.div>
                );
              })()}

              {/* Sign Out */}
              <button
                onClick={handleSignOut}
                className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors mt-4"
              >
                <ArrowRightOnRectangleIcon className="w-5 h-5" />
                <span className="font-medium">{t("nav.signOut")}</span>
              </button>
            </div>
          </aside>
        )}

        {/* Sidebar - Mobile */}
        <AnimatePresence>
          {isSidebarOpen && (
            <>
              {/* Overlay */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsSidebarOpen(false)}
                className="lg:hidden fixed inset-0 bg-black/50 z-40 overscroll-contain"
              />

              {/* Sidebar */}
              <motion.aside
                initial={{ x: "-100%" }}
                animate={{ x: 0 }}
                exit={{ x: "-100%" }}
                transition={{ type: "spring", damping: 25, stiffness: 200 }}
                className="lg:hidden fixed left-0 top-0 h-full w-64 bg-white shadow-2xl z-50 overflow-y-auto overscroll-contain"
              >
                <div className="p-4">
                  {/* Close Button */}
                  <div className="flex justify-end mb-4">
                    <button
                      onClick={() => setIsSidebarOpen(false)}
                      className="p-2 rounded-lg hover:bg-nieve focus:outline-none focus-visible:ring-2 focus-visible:ring-tiber-500 focus-visible:ring-offset-2 transition-colors"
                      aria-label={t("common.close")}
                    >
                      <XMarkIcon
                        className="w-6 h-6 text-juridico"
                        aria-hidden
                      />
                    </button>
                  </div>

                  {/* Logo */}
                  <div className="flex items-center space-x-2 mb-6 px-2">
                    <SparklesIcon className="w-8 h-8 text-tiber-500" />
                    <span className="text-xl font-display font-bold text-juridico">
                      Lawgentia
                    </span>
                  </div>

                  {/* Items */}
                  {!loadingProfile && !loading && profile && (
                    <div className="space-y-2">
                      {sidebarItems.map((item, index) => (
                        <SidebarItem
                          key={index}
                          {...item}
                          isActive={location.pathname === item.href}
                          onClick={() => handleNavigation(item.href || "")}
                        />
                      ))}

                      {/* Upgrade Banner - Smart */}
                      {(() => {
                        const currentPlanType = subscription?.planType || null;
                        const userType = profile?.user_type || null;
                        const nextPlan = getNextPlan(currentPlanType, userType);

                        if (!nextPlan) return null; // Already on highest plan or no plan data

                        const planName = formatPlanName(nextPlan);

                        return (
                          <div className="mt-6 p-4 bg-linear-to-br from-dorado to-dorado-dark rounded-xl text-white">
                            <h3 className="font-bold mb-2">
                              ✨ {t("dashboard.sidebar.upgrade", { plan: planName })}
                            </h3>
                            <p className="text-sm mb-3 opacity-90">
                              {t("dashboard.sidebar.upgradeMessage", {
                                plan: planName,
                              })}
                            </p>
                            <button
                              onClick={() => {
                                navigate(ROUTES.PRICING);
                                setIsSidebarOpen(false);
                              }}
                              className="w-full bg-white text-dorado font-semibold py-2 rounded-lg"
                            >
                              {t("dashboard.sidebar.upgradeButton")}
                            </button>
                          </div>
                        );
                      })()}

                      {/* Sign Out */}
                      <button
                        onClick={handleSignOut}
                        className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors mt-4"
                      >
                        <ArrowRightOnRectangleIcon className="w-5 h-5" />
                        <span className="font-medium">{t("nav.signOut")}</span>
                      </button>
                    </div>
                  )}
                </div>
              </motion.aside>
            </>
          )}
        </AnimatePresence>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto bg-white overflow-x-hidden">
          <div className="p-4 sm:p-6 lg:p-8">
            <EmailVerificationBanner />
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};
